import { SourceModel } from '@wolf/entities'

type NutritionFacts = {
  [key: string]: string
  energy: string
  totalFat: string
  saturatedFat: string
  hydrates: string
  sugars: string
  fiber: string
  proteins: string
  salt: string
}

type Allergens = {
  [key: string]: string
  peanuts: string
  mustard: string
  soybeans: string
  sesame: string
  crustaceans: string
  milk: string
  egg: string
  fish: string
  sulphur: string
  molluscs: string
  gluten: string
  nuts: string
  celery: string
  lupine: string
}

type Translation = {
  expirationDate: string
  protectAtmosphere: string
  macsaWarning: string
  allergensWarning: string
  nutritionFactsTitle: string
  per100g: string
  serving: string
  ingredients: string
  notHeat: string
  netWeight: string
  allergens: Allergens
  nutritionFacts: NutritionFacts
}

const translations: Record<'ES' | 'DE' | 'FR' | 'NL', Translation> = {
  ES: {
    expirationDate: 'Fecha de caducidad',
    protectAtmosphere: 'Envasado en atmósfera protectora. Una vez abierto, consumir antes de 24 horas.',
    macsaWarning: 'Una vez abierto, consumir antes de: ver envase.',
    allergensWarning: 'Puede contener trazas de:',
    nutritionFactsTitle: 'Información nutricional',
    per100g: 'Por cada 100 g',
    serving: 'Ración',
    ingredients: 'Ingredientes',
    notHeat: 'No calentar',
    netWeight: 'Peso neto',
    allergens: {
      peanuts: 'Cacahuetes',
      mustard: 'Mostaza',
      soybeans: 'Soja',
      sesame: 'Sésamo',
      crustaceans: 'Crustáceos',
      milk: 'Lácteos',
      egg: 'Huevos',
      fish: 'Pescado',
      sulphur: 'Sulfitos',
      molluscs: 'Moluscos',
      gluten: 'Gluten',
      nuts: 'Frutos de cáscara',
      celery: 'Apio',
      lupine: 'Altramuces',
    },
    nutritionFacts: {
      energy: 'Valor energético',
      totalFat: 'Grasas',
      saturatedFat: 'de las cuales saturadas',
      hydrates: 'Hidratos de carbono',
      sugars: 'de los cuales azúcares',
      fiber: 'Fibra alimentaria',
      proteins: 'Proteínas',
      salt: 'Sal',
    },
  },
  DE: {
    expirationDate: 'Verfallsdatum',
    protectAtmosphere: 'Unter Schutzatmosphäre verpackt. Nach dem Öffnen innerhalb von 24 Stunden verbrauchen.',
    macsaWarning: 'Nach dem Öffnen bis zum: siehe Verpackung verbrauchen.',
    allergensWarning: 'Kann Spuren enthalten von:',
    nutritionFactsTitle: 'Nährwerte',
    per100g: 'Pro 100 g',
    serving: 'Portion',
    ingredients: 'Zutaten',
    notHeat: 'Nicht erwärmen',
    netWeight: 'Nettogewicht',
    allergens: {
      peanuts: 'Erdnüsse',
      mustard: 'Senf',
      soybeans: 'Soja',
      sesame: 'Sesam',
      crustaceans: 'Krustentiere',
      milk: 'Milch',
      egg: 'Ei',
      fish: 'Fisch',
      sulphur: 'Sulfite',
      molluscs: 'Weichtiere',
      gluten: 'Gluten',
      nuts: 'Nüsse',
      celery: 'Sellerie',
      lupine: 'Lupinen',
    },
    nutritionFacts: {
      energy: 'Energie',
      totalFat: 'Gesamtfett',
      saturatedFat: 'Gesättigte Fette',
      hydrates: 'Kohlenhydrate',
      sugars: 'Zucker',
      fiber: 'Ballaststoffe',
      proteins: 'Proteine',
      salt: 'Salz',
    },
  },
  FR: {
    expirationDate: 'Date de péremption',
    protectAtmosphere: 'Emballé sous atmosphère protectrice. Une fois ouvert, consommer dans les 24 heures.',
    macsaWarning: 'Après ouverture, à consommer avant : voir emballage.',
    allergensWarning: 'Peut contenir des traces de:',
    nutritionFactsTitle: 'Valeurs nutritionnelles',
    per100g: 'Pour 100 g',
    serving: 'Portion',
    ingredients: 'Ingrédients',
    notHeat: 'Ne pas chauffer',
    netWeight: 'Poids net',
    allergens: {
      peanuts: 'Arachides',
      mustard: 'Moutarde',
      soybeans: 'Soja',
      sesame: 'Sésame',
      crustaceans: 'Crustacés',
      milk: 'Produits laitiers',
      egg: 'Œufs',
      fish: 'Poisson',
      sulphur: 'Sulfites',
      molluscs: 'Mollusques',
      gluten: 'Gluten',
      nuts: 'Fruits à coque',
      celery: 'Céleri',
      lupine: 'Lupin',
    },
    nutritionFacts: {
      energy: 'Énergie',
      totalFat: 'Matières grasses',
      saturatedFat: 'Graisses saturées',
      hydrates: 'Glucides',
      sugars: 'Sucres',
      fiber: 'Fibres alimentaires',
      proteins: 'Protéines',
      salt: 'Sel',
    },
  },
  NL: {
    expirationDate: 'Houdbaarheidsdatum',
    protectAtmosphere: 'Verpakt onder beschermende atmosfeer. Na openen binnen 24 uur consumeren.',
    macsaWarning: 'Na opening te gebruiken vóór: zie verpakking.',
    allergensWarning: 'Kan sporen bevatten van:',
    nutritionFactsTitle: 'Voedingswaarden',
    per100g: 'Per 100 g',
    serving: 'Portie',
    ingredients: 'Ingrediënten',
    notHeat: 'Niet verwarmen',
    netWeight: 'Nettogewicht',
    allergens: {
      peanuts: 'Pinda’s',
      mustard: 'Mosterd',
      soybeans: 'Sojabonen',
      sesame: 'Sesam',
      crustaceans: 'Schaaldieren',
      milk: 'Zuivelproducten',
      egg: 'Eieren',
      fish: 'Vis',
      sulphur: 'Sulfieten',
      molluscs: 'Weekdieren',
      gluten: 'Gluten',
      nuts: 'Noten',
      celery: 'Selderij',
      lupine: 'Lupine',
    },
    nutritionFacts: {
      energy: 'Energie',
      totalFat: 'Totaal vet',
      saturatedFat: 'Verzadigd vet',
      hydrates: 'Koolhydraten',
      sugars: 'Suikers',
      fiber: 'Voedingsvezels',
      proteins: 'Eiwitten',
      salt: 'Zout',
    },
  },
}

export const getTranslations = (language: SourceModel['language']) => {
  const safeLanguage = language ?? 'ES'
  return translations[safeLanguage] || translations['ES']
}
